/**
 * ONE TAP — snapshot buffering and remote-entity interpolation.
 *
 * The server ticks at 30Hz. The client renders at 144. Everything the local
 * player does not own — the four other bodies and, critically, the gun arcing
 * through the air — is reconstructed here by rendering INTERP_DELAY_MS in the
 * past and blending the two snapshots that bracket that instant.
 *
 * Design notes that matter:
 *
 *  - The thrown gun is the most-watched object on the screen. It gets the same
 *    treatment as players: position, velocity and spin all interpolate, and
 *    while starved it extrapolates along a *gravity-correct* arc rather than a
 *    straight line, because a mid-flight pass is exactly when a straight-line
 *    guess reads as a stutter.
 *  - Extrapolation is capped (EXTRAPOLATE_MAX_MS) and then freezes. Freezing
 *    looks like lag; flying off looks like a bug.
 *  - Non-interpolatable fields (health, gun state, move state) snap to whichever
 *    snapshot is nearer in time, so a state change never appears a full tick early.
 *  - The local player is never touched — prediction owns that body.
 */

import {
  GRAVITY,
  GUN_GRAVITY_SCALE,
  SNAPSHOT_BUFFER_SIZE,
  THROW_SPEED_CHARGED,
  TICK_DT,
} from '@shared/constants';
import { EPSILON, clamp01, lerpAngle, lerpN, type Vec3 } from '@shared/math';
import {
  GunState,
  MoveState,
  RoundPhase,
  type GunEntity,
  type MatchState,
  type PlayerState,
  type SnapshotPayload,
  type WorldState,
} from '@shared/types';
import { createPlayer } from '@shared/sim/world';
// A blank gun comes from the sim rather than a local literal, so a new GunEntity
// field can never be silently dropped by the snapshot buffer.
import { createGunEntity } from '@shared/sim/match';

/**
 * How far past the newest snapshot we are willing to guess. Two lost packets is
 * ~66ms; 120ms covers a hiccup without ever letting a body slide through a wall.
 */
const EXTRAPOLATE_MAX_MS = 120;

/**
 * Above this per-snapshot displacement it was a teleport (round reset, gun
 * respawn), not motion — cut instead of sliding the entity across the arena.
 * Derived from the fastest thing in the game over four ticks.
 */
const TELEPORT_DISTANCE = THROW_SPEED_CHARGED * TICK_DT * 4;

interface Snap {
  tick: number;
  timeMs: number;
  state: WorldState;
}

type AnyRec = Record<string, unknown>;

function isVec(v: unknown): v is Vec3 {
  if (typeof v !== 'object' || v === null) return false;
  const c = v as Partial<Vec3>;
  return typeof c.x === 'number' && typeof c.y === 'number' && typeof c.z === 'number';
}

/**
 * Structural copy for the plain-data state tree. Deliberately key-walking rather
 * than a hand-written field list: WorldState is a shared contract that other
 * agents extend, and a copier that silently drops a new field would produce
 * bugs that look like netcode desync.
 */
function assignDeep(dst: AnyRec, src: AnyRec): void {
  for (const k in src) {
    const v = src[k];
    // A delta patch simply omits unchanged fields; an explicit null is meaningful
    // (wallNormal, grapplePoint, holder) and must be written through.
    if (v === undefined) continue;
    if (v === null) {
      dst[k] = null;
      continue;
    }
    if (typeof v !== 'object') {
      dst[k] = v;
      continue;
    }
    if (Array.isArray(v)) {
      let arr = dst[k];
      if (!Array.isArray(arr)) {
        arr = [];
        dst[k] = arr;
      }
      const out = arr as unknown[];
      out.length = v.length;
      for (let i = 0; i < v.length; i++) {
        const e = v[i];
        if (e !== null && typeof e === 'object' && !Array.isArray(e)) {
          let de = out[i];
          if (de === null || typeof de !== 'object') {
            de = {};
            out[i] = de;
          }
          assignDeep(de as AnyRec, e as AnyRec);
        } else {
          out[i] = e;
        }
      }
      continue;
    }
    if (isVec(v)) {
      const d = dst[k];
      if (isVec(d)) {
        d.x = v.x;
        d.y = v.y;
        d.z = v.z;
      } else {
        dst[k] = { x: v.x, y: v.y, z: v.z };
      }
      continue;
    }
    let d = dst[k];
    if (d === null || typeof d !== 'object') {
      d = {};
      dst[k] = d;
    }
    assignDeep(d as AnyRec, v as AnyRec);
  }
}

function blankMatch(): MatchState {
  return {
    phase: RoundPhase.Lobby,
    round: 0,
    scoreA: 0,
    scoreB: 0,
    phaseEndsAtMs: 0,
    roundEndsAtMs: 0,
    suddenDeath: false,
    lastRoundWinner: -1,
    winner: -1,
  };
}

/**
 * An empty world the client can render (and patch snapshots into) before the
 * first `welcome` lands. Exported because main.ts needs one to exist at boot.
 */
export function createEmptyWorldState(): WorldState {
  return { tick: 0, timeMs: 0, players: {}, guns: [], match: blankMatch() };
}

/**
 * Patch a delta (or keyframe) snapshot payload into a running WorldState.
 *
 * Lives here rather than in main.ts because it is the exact inverse of what the
 * server's delta encoder does, and the two have to be read side by side.
 */
export function applySnapshot(out: WorldState, tick: number, timeMs: number, payload: SnapshotPayload): void {
  out.tick = tick;
  out.timeMs = timeMs;

  const players = payload.players ?? {};

  if (payload.full) {
    for (const id in out.players) {
      if (!(id in players)) delete out.players[id];
    }
  }

  for (const id in players) {
    const patch = players[id]!;
    let p = out.players[id];
    if (!p) {
      p = createPlayer(id, patch.name ?? '', patch.team ?? 0, patch.bot ?? false);
      out.players[id] = p;
    }
    assignDeep(p as unknown as AnyRec, patch as unknown as AnyRec);
  }

  if (payload.removed) {
    for (const id of payload.removed) delete out.players[id];
  }

  const guns = payload.guns ?? [];
  if (payload.full) out.guns.length = guns.length;
  for (let i = 0; i < guns.length; i++) {
    const patch = guns[i]!;
    let g = out.guns[i];
    if (!g) {
      g = createGunEntity(patch.id ?? i, patch.team ?? 0);
      out.guns[i] = g;
    }
    assignDeep(g as unknown as AnyRec, patch as unknown as AnyRec);
  }

  if (payload.match) assignDeep(out.match as unknown as AnyRec, payload.match as unknown as AnyRec);
}

export class SnapshotInterpolator {
  private readonly ring: Snap[] = [];
  private head = 0;
  private count = 0;

  constructor() {
    for (let i = 0; i < SNAPSHOT_BUFFER_SIZE; i++) {
      this.ring.push({ tick: -1, timeMs: 0, state: createEmptyWorldState() });
    }
  }

  /** Number of buffered snapshots. Useful for a "buffering…" indicator. */
  get size(): number {
    return this.count;
  }

  /** Server timestamp of the newest buffered snapshot, or 0. */
  get newestTimeMs(): number {
    return this.count > 0 ? this.at(this.count - 1).timeMs : 0;
  }

  clear(): void {
    this.head = 0;
    this.count = 0;
  }

  /**
   * Store a deep copy of `state`. The caller keeps mutating its own world object
   * as deltas arrive, so aliasing here would rewrite history.
   */
  push(tick: number, timeMs: number, state: WorldState): void {
    // WebSocket delivery is ordered, but a reconnect can hand us an older world.
    // Rewinding the buffer is worse than dropping the frame.
    if (this.count > 0) {
      const newest = this.at(this.count - 1);
      if (timeMs <= newest.timeMs) {
        if (tick < newest.tick) this.clear();
        else return;
      }
    }

    if (this.count === SNAPSHOT_BUFFER_SIZE) {
      this.head = (this.head + 1) % SNAPSHOT_BUFFER_SIZE;
      this.count--;
    }
    const slot = this.ring[(this.head + this.count) % SNAPSHOT_BUFFER_SIZE]!;
    slot.tick = tick;
    slot.timeMs = timeMs;
    this.copyWorld(slot.state, state);
    this.count++;
  }

  latest(): WorldState | null {
    return this.count > 0 ? this.at(this.count - 1).state : null;
  }

  /**
   * Reconstruct the world as it looked at `renderTimeMs` (server clock) into
   * `out`. The local player's body is left exactly as prediction left it.
   */
  sample(renderTimeMs: number, out: WorldState, excludePlayerId: string): void {
    if (this.count === 0) return;

    const oldest = this.at(0);
    const newest = this.at(this.count - 1);

    let a: Snap;
    let b: Snap;
    let t = 0;
    let extrapSec = 0;

    if (renderTimeMs >= newest.timeMs) {
      // Starved: guess forward, but only briefly, then hold. Clamping the
      // extrapolation window is what turns a stall into a freeze instead of
      // sending everyone sliding through the arena walls.
      a = newest;
      b = newest;
      extrapSec = Math.min(renderTimeMs - newest.timeMs, EXTRAPOLATE_MAX_MS) / 1000;
    } else if (renderTimeMs <= oldest.timeMs) {
      a = oldest;
      b = oldest;
    } else {
      let i = this.count - 1;
      while (i > 0 && this.at(i).timeMs > renderTimeMs) i--;
      a = this.at(i);
      b = this.at(Math.min(i + 1, this.count - 1));
      const span = b.timeMs - a.timeMs;
      t = span > EPSILON ? clamp01((renderTimeMs - a.timeMs) / span) : 0;
    }

    const near = t < 0.5 ? a.state : b.state;

    // --- players -----------------------------------------------------------
    for (const id in b.state.players) {
      const pb = b.state.players[id]!;
      const pa = a.state.players[id] ?? pb;
      let dst = out.players[id];
      if (!dst) {
        dst = createPlayer(id, pb.name, pb.team, pb.bot);
        out.players[id] = dst;
        if (id === excludePlayerId) {
          // Bootstrap the local body once, then never touch it again.
          assignDeep(dst as unknown as AnyRec, pb as unknown as AnyRec);
        }
      }
      if (id === excludePlayerId) continue;

      const src = near.players[id] ?? pb;
      assignDeep(dst as unknown as AnyRec, src as unknown as AnyRec);
      this.blendPlayer(dst, pa, pb, t, extrapSec);
    }

    // Players that left the match stop existing. The local body is exempt: it is
    // owned by prediction and may legitimately outlive a snapshot gap.
    for (const id in out.players) {
      if (id === excludePlayerId) continue;
      if (!(id in b.state.players)) delete out.players[id];
    }

    // --- guns --------------------------------------------------------------
    const gunsB = b.state.guns;
    out.guns.length = gunsB.length;
    for (let i = 0; i < gunsB.length; i++) {
      const gb = gunsB[i]!;
      const ga = this.findGun(a.state, gb.id, i) ?? gb;
      let dst = out.guns[i];
      if (!dst) {
        dst = createGunEntity(gb.id, gb.team);
        out.guns[i] = dst;
      }
      const src = this.findGun(near, gb.id, i) ?? gb;
      assignDeep(dst as unknown as AnyRec, src as unknown as AnyRec);
      this.blendGun(dst, ga, gb, t, extrapSec);
    }

    // --- match -------------------------------------------------------------
    assignDeep(out.match as unknown as AnyRec, near.match as unknown as AnyRec);
    out.tick = near.tick;
    out.timeMs = renderTimeMs;
  }

  // -------------------------------------------------------------------------

  private at(i: number): Snap {
    return this.ring[(this.head + i) % SNAPSHOT_BUFFER_SIZE]!;
  }

  private findGun(w: WorldState, id: number, hint: number): GunEntity | null {
    const guess = w.guns[hint];
    if (guess && guess.id === id) return guess;
    for (const g of w.guns) if (g.id === id) return g;
    return null;
  }

  private copyWorld(dst: WorldState, src: WorldState): void {
    dst.tick = src.tick;
    dst.timeMs = src.timeMs;
    for (const id in dst.players) {
      if (!(id in src.players)) delete dst.players[id];
    }
    for (const id in src.players) {
      const sp = src.players[id]!;
      let dp = dst.players[id];
      if (!dp) {
        dp = createPlayer(id, sp.name, sp.team, sp.bot);
        dst.players[id] = dp;
      }
      assignDeep(dp as unknown as AnyRec, sp as unknown as AnyRec);
    }
    dst.guns.length = src.guns.length;
    for (let i = 0; i < src.guns.length; i++) {
      const sg = src.guns[i]!;
      let dg = dst.guns[i];
      if (!dg) {
        dg = createGunEntity(sg.id, sg.team);
        dst.guns[i] = dg;
      }
      assignDeep(dg as unknown as AnyRec, sg as unknown as AnyRec);
    }
    assignDeep(dst.match as unknown as AnyRec, src.match as unknown as AnyRec);
  }

  private blendPlayer(dst: PlayerState, a: PlayerState, b: PlayerState, t: number, extrapSec: number): void {
    const teleported =
      Math.abs(a.pos.x - b.pos.x) > TELEPORT_DISTANCE ||
      Math.abs(a.pos.y - b.pos.y) > TELEPORT_DISTANCE ||
      Math.abs(a.pos.z - b.pos.z) > TELEPORT_DISTANCE;

    if (teleported) {
      dst.pos.x = b.pos.x;
      dst.pos.y = b.pos.y;
      dst.pos.z = b.pos.z;
      dst.vel.x = b.vel.x;
      dst.vel.y = b.vel.y;
      dst.vel.z = b.vel.z;
      dst.yaw = b.yaw;
      dst.pitch = b.pitch;
      return;
    }

    dst.pos.x = lerpN(a.pos.x, b.pos.x, t);
    dst.pos.y = lerpN(a.pos.y, b.pos.y, t);
    dst.pos.z = lerpN(a.pos.z, b.pos.z, t);
    dst.vel.x = lerpN(a.vel.x, b.vel.x, t);
    dst.vel.y = lerpN(a.vel.y, b.vel.y, t);
    dst.vel.z = lerpN(a.vel.z, b.vel.z, t);
    // Yaw wraps; pitch is clamped well inside +-PI but the shortest-arc form is
    // correct for both and costs nothing.
    dst.yaw = lerpAngle(a.yaw, b.yaw, t);
    dst.pitch = lerpAngle(a.pitch, b.pitch, t);

    if (extrapSec > 0) {
      dst.pos.x += dst.vel.x * extrapSec;
      dst.pos.y += dst.vel.y * extrapSec;
      dst.pos.z += dst.vel.z * extrapSec;
      // Only a free-falling body has a predictable vertical arc; wall-running,
      // grappling and sliding all fight gravity, so leave those linear.
      if (b.moveState === MoveState.Airborne) {
        dst.pos.y += 0.5 * GRAVITY * extrapSec * extrapSec;
        dst.vel.y += GRAVITY * extrapSec;
      }
    }
  }

  private blendGun(dst: GunEntity, a: GunEntity, b: GunEntity, t: number, extrapSec: number): void {
    const teleported =
      Math.abs(a.pos.x - b.pos.x) > TELEPORT_DISTANCE ||
      Math.abs(a.pos.y - b.pos.y) > TELEPORT_DISTANCE ||
      Math.abs(a.pos.z - b.pos.z) > TELEPORT_DISTANCE ||
      a.state !== b.state;

    if (teleported) {
      dst.pos.x = b.pos.x;
      dst.pos.y = b.pos.y;
      dst.pos.z = b.pos.z;
      dst.vel.x = b.vel.x;
      dst.vel.y = b.vel.y;
      dst.vel.z = b.vel.z;
      dst.rot.x = b.rot.x;
      dst.rot.y = b.rot.y;
      dst.rot.z = b.rot.z;
      return;
    }

    dst.pos.x = lerpN(a.pos.x, b.pos.x, t);
    dst.pos.y = lerpN(a.pos.y, b.pos.y, t);
    dst.pos.z = lerpN(a.pos.z, b.pos.z, t);
    dst.vel.x = lerpN(a.vel.x, b.vel.x, t);
    dst.vel.y = lerpN(a.vel.y, b.vel.y, t);
    dst.vel.z = lerpN(a.vel.z, b.vel.z, t);
    dst.rot.x = lerpAngle(a.rot.x, b.rot.x, t);
    dst.rot.y = lerpAngle(a.rot.y, b.rot.y, t);
    dst.rot.z = lerpAngle(a.rot.z, b.rot.z, t);

    if (extrapSec > 0 && b.state === GunState.InFlight) {
      const g = GRAVITY * GUN_GRAVITY_SCALE;
      dst.pos.x += dst.vel.x * extrapSec;
      dst.pos.y += dst.vel.y * extrapSec + 0.5 * g * extrapSec * extrapSec;
      dst.pos.z += dst.vel.z * extrapSec;
      dst.vel.y += g * extrapSec;
      // Keep the tumble going too — a gun that freezes its spin mid-pass reads
      // as a dropped frame even when the position is perfect.
      dst.rot.x += b.spin.x * extrapSec;
      dst.rot.y += b.spin.y * extrapSec;
      dst.rot.z += b.spin.z * extrapSec;
    }
  }
}
